import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconEdit,
  IconGitMerge,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import {
  useAddAlias,
  useParty,
  usePartyMentions,
  useRemoveAlias,
  useSoftDeleteParty,
} from '../../queries/parties';
import { PartyFormDrawer } from './PartyFormDrawer';
import { MergePartyDrawer } from './MergePartyDrawer';
import { useProjectStore } from '../../project/project-store';

export function PartyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';

  const { data, isLoading, isError } = useParty(id);
  const { data: mentions = [], isLoading: isMentionsLoading } = usePartyMentions(id);

  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [aliasInput, setAliasInput] = useState('');

  const addAlias = useAddAlias();
  const removeAlias = useRemoveAlias();
  const softDelete = useSoftDeleteParty();

  if (isLoading) {
    return <Text c="dimmed">Loading…</Text>;
  }
  if (isError || !data || !id) {
    return (
      <Stack>
        <Anchor component={Link} to="/parties" size="sm">
          <IconArrowLeft size={14} /> Back to parties
        </Anchor>
        <Alert color="red">Party not found.</Alert>
      </Stack>
    );
  }

  const { party, aliases } = data;

  async function handleAddAlias() {
    const value = aliasInput.trim();
    if (!value || !id) return;
    try {
      await addAlias.mutateAsync({ party_id: id, alias: value });
      setAliasInput('');
    } catch (err) {
      notifications.show({
        title: 'Could not add alias',
        message: err instanceof Error ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  function handleSoftDelete() {
    modals.openConfirmModal({
      title: 'Delete party',
      children: (
        <Text>
          Soft-delete <b>{party.canonical_name}</b>? Existing runsheet attachments stay (history
          is preserved) but the party will no longer appear in pickers or search.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await softDelete.mutateAsync(party.id);
        notifications.show({ title: 'Deleted', message: party.canonical_name, color: 'pine' });
        navigate('/parties');
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Anchor component={Link} to="/parties" size="sm" c="dimmed">
            <IconArrowLeft size={14} /> All parties
          </Anchor>
          <Title order={2}>{party.canonical_name}</Title>
          <Group gap="xs">
            <Badge variant="light" tt="lowercase">
              {party.party_type}
            </Badge>
            <Text size="xs" c="dimmed">
              created {dayjs(party.created_at).format('YYYY-MM-DD')}
            </Text>
          </Group>
        </Stack>
        {canWrite && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconEdit size={14} />}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="default"
              leftSection={<IconGitMerge size={14} />}
              onClick={() => setMergeOpen(true)}
            >
              Merge into…
            </Button>
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={handleSoftDelete}
            >
              Delete
            </Button>
          </Group>
        )}
      </Group>

      {party.notes && (
        <Card withBorder padding="sm" radius="md">
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {party.notes}
          </Text>
        </Card>
      )}

      {/* Aliases */}
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="xs">
          <Title order={5}>Aliases</Title>
          <Text size="xs" c="dimmed">
            {aliases.length} known
          </Text>
        </Group>
        <Group gap="xs" wrap="wrap">
          {aliases.length === 0 && (
            <Text size="sm" c="dimmed">
              No aliases yet.
            </Text>
          )}
          {aliases.map((a) => (
            <Badge
              key={a.id}
              variant="light"
              size="lg"
              rightSection={
                canWrite ? (
                  <Tooltip label="Remove alias">
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      color="gray"
                      onClick={() => removeAlias.mutate({ party_id: party.id, alias_id: a.id })}
                      aria-label="remove"
                    >
                      <IconX size={12} />
                    </ActionIcon>
                  </Tooltip>
                ) : null
              }
            >
              {a.alias}
            </Badge>
          ))}
        </Group>
        {canWrite && (
          <Group mt="sm" gap="xs">
            <TextInput
              placeholder="Add a new alias"
              value={aliasInput}
              onChange={(e) => setAliasInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddAlias();
              }}
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconPlus size={14} />}
              onClick={handleAddAlias}
              disabled={!aliasInput.trim()}
              loading={addAlias.isPending}
            >
              Add
            </Button>
          </Group>
        )}
      </Card>

      {/* Mentions */}
      <Card withBorder padding="md" radius="md">
        <Group justify="space-between" mb="xs">
          <Title order={5}>Mentions</Title>
          <Text size="xs" c="dimmed">
            {mentions.length} runsheet entr{mentions.length === 1 ? 'y' : 'ies'}
          </Text>
        </Group>

        <DataTable
          minHeight={120}
          fetching={isMentionsLoading}
          records={mentions}
          idAccessor="attachment_id"
          noRecordsText="This party isn't attached to any runsheet entry yet."
          columns={[
            {
              accessor: 'role',
              title: 'Role',
              width: 100,
              render: (m) => (
                <Badge variant="light" tt="lowercase">
                  {m.role}
                </Badge>
              ),
            },
            {
              accessor: 'document_type',
              title: 'Document',
              render: (m) => (
                <Stack gap={0}>
                  <Text size="sm">
                    {m.document_type_name ?? <Text span c="dimmed">(no type)</Text>}
                  </Text>
                  {m.instrument_number && (
                    <Text size="xs" c="dimmed">
                      #{m.instrument_number}
                    </Text>
                  )}
                </Stack>
              ),
            },
            {
              accessor: 'instrument_date',
              title: 'Date',
              width: 120,
              render: (m) =>
                m.instrument_date
                  ? dayjs(m.instrument_date).format('YYYY-MM-DD')
                  : <Text c="dimmed">—</Text>,
            },
            {
              accessor: 'book_page',
              title: 'Book / Page',
              width: 110,
              render: (m) =>
                m.book || m.page ? `${m.book ?? '—'} / ${m.page ?? '—'}` : <Text c="dimmed">—</Text>,
            },
            {
              accessor: 'context',
              title: 'Tract / Title run',
              render: (m) => (
                <Stack gap={0}>
                  <Anchor component={Link} to={`/title-runs/${m.title_run_id}`} size="sm">
                    {m.title_run_title}
                  </Anchor>
                  <Text size="xs" c="dimmed">
                    {m.tract_name}
                  </Text>
                </Stack>
              ),
            },
          ]}
        />
      </Card>

      <PartyFormDrawer
        opened={editOpen}
        onClose={() => setEditOpen(false)}
        party={party}
      />
      {canWrite && (
        <MergePartyDrawer
          opened={mergeOpen}
          onClose={() => setMergeOpen(false)}
          survivor={party}
        />
      )}
    </Stack>
  );
}
